'use client';

import React from 'react';
import styled, { css } from 'styled-components';

export type PageHeaderVariant = 'canvas' | 'content';

export interface PageHeaderProps {
  /** Page title — H2 22 px on `canvas`, H1 32 px on `content`. */
  title: string;
  /** Optional one-line subtitle below the title. */
  subtitle?: string;
  /** Optional uppercase eyebrow above the title (e.g. `"Booking"`, `"Vehicle"`). */
  eyebrow?: string;
  /** Right-aligned action node — typically a row of `Button`s. */
  actions?: React.ReactNode;
  /**
   * Visual rhythm:
   * - `canvas` (default): H2 title, compact spacing — for list/dashboard/canvas screens.
   * - `content`: H1 title, step-up rhythm — for record-detail screens (one record at a time).
   *
   * See `liftango-ui-ops/references/layout.mdx` for when each variant applies.
   */
  variant?: PageHeaderVariant;
  className?: string;
}

/**
 * Standard page header for content and canvas screens. Owns title size,
 * eyebrow placement, subtitle treatment, and the bottom border that
 * separates the head from the body region.
 *
 * Encodes the rules from `liftango-ui-ops/references/layout.mdx`:
 *
 * - `variant="content"` bumps the title from H2 22 px to H1 32 px and
 *   widens the bottom margin — for screens carrying *one record at a time*.
 * - `variant="canvas"` (default) is the compact layout for list, dashboard,
 *   and canvas screens.
 * - The `actions` slot holds *meta* actions (back, replicate, open). Commit
 *   actions live in an `ActionBar` further down the page, never here.
 */
export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
  variant = 'canvas',
  className,
}: PageHeaderProps) {
  return (
    <Wrap $variant={variant} className={className}>
      <Block>
        {eyebrow && <Eyebrow data-testid="page-header-eyebrow">{eyebrow}</Eyebrow>}
        {variant === 'content' ? (
          <ContentTitle>{title}</ContentTitle>
        ) : (
          <CanvasTitle>{title}</CanvasTitle>
        )}
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Block>
      {actions && <Actions>{actions}</Actions>}
    </Wrap>
  );
}

const Wrap = styled.header<{ $variant: PageHeaderVariant }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${(p) => p.theme.spaceAlias.lg};
  padding-bottom: ${(p) => p.theme.spaceAlias.md};
  border-bottom: 1px solid ${(p) => p.theme.colors.neutral.line};
  ${(p) =>
    p.$variant === 'content'
      ? css`
          margin-bottom: ${p.theme.spaceAlias.lg};
        `
      : css`
          margin-bottom: ${p.theme.spaceAlias.md};
        `}
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Eyebrow = styled.div`
  font-size: ${(p) => p.theme.fontSize.eyebrow};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  margin-bottom: 4px;
`;

const CanvasTitle = styled.h2`
  margin: 0;
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.h2};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  line-height: 1.2;
  color: ${(p) => p.theme.colors.neutral.ink};
`;

const ContentTitle = styled.h1`
  margin: 0;
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.h1};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  line-height: 1.15;
  color: ${(p) => p.theme.colors.neutral.ink};
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  max-width: 60ch;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
  flex-shrink: 0;
`;
